import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Edit2, Trash2, AlertCircle, Building, Loader, Database, ShoppingBag, Search, Globe, ChevronDown, X } from 'lucide-react';
import config from '../config';
import BulleSurvol from './BulleSurvol';
import '../styles/compteur.css';
import googleAdsLogo from '../logos/google-ads.png';
import microsoftAdsLogo from '../logos/microsoft-ads.png';
import metaAdsLogo from '../logos/meta-ads.png';
import pianoLogo from '../logos/piano-analytics.png';
import matomoLogo from '../logos/matomo.png';
import chatgptAdsLogo from '../logos/chatgpt-ads.png';
import awinLogo from '../logos/awin.png';
import prestashopLogo from '../logos/prestashop.png';
import tiktokAdsLogo from '../logos/tiktok-ads.png';
import pinterestAdsLogo from '../logos/pinterest-ads.png';
import searchConsoleLogo from '../logos/google-search-console.png';
import googleAnalyticsLogo from '../logos/google-analytics.svg';
import merchantLogo from '../logos/google-merchant.png';
import tagManagerLogo from '../logos/google-tag-manager.png';
import CreateSiteModal from './kanban/modals/CreateSiteModal';

/** Le logo d'une plateforme, pour la bulle des comptes rattachés. */
const LOGOS_PROVIDER = {
  GOOGLE_ADS: googleAdsLogo,
  MICROSOFT_ADS: microsoftAdsLogo,
  FACEBOOK_ADS: metaAdsLogo,
  PINTEREST_ADS: pinterestAdsLogo,
  TIKTOK_ADS: tiktokAdsLogo,
  OPENAI_ADS: chatgptAdsLogo,
  AWIN: awinLogo,
  PRESTASHOP: prestashopLogo,
  MATOMO: matomoLogo,
  PIANO_ANALYTICS: pianoLogo,
  GOOGLE_ANALYTICS: googleAnalyticsLogo,
  GOOGLE_SEARCH_CONSOLE: searchConsoleLogo,
  GOOGLE_TAG_MANAGER: tagManagerLogo,
  GOOGLE_MERCHANT_API: merchantLogo
};

/** Le nom lisible d'une plateforme, pour la bulle des comptes rattachés. */
const LIBELLES_PROVIDER = {
  GOOGLE_ADS: 'Google Ads',
  MICROSOFT_ADS: 'Microsoft Ads',
  FACEBOOK_ADS: 'Meta Ads',
  PINTEREST_ADS: 'Pinterest Ads',
  TIKTOK_ADS: 'TikTok Ads',
  OPENAI_ADS: 'ChatGPT Ads',
  AWIN: 'Awin',
  PRESTASHOP: 'PrestaShop',
  MATOMO: 'Matomo',
  PIANO_ANALYTICS: 'Piano Analytics',
  GOOGLE_ANALYTICS: 'Google Analytics 4',
  GOOGLE_SEARCH_CONSOLE: 'Search Console',
  GOOGLE_TAG_MANAGER: 'Tag Manager',
  GOOGLE_MERCHANT_API: 'Merchant Center',
  GOOGLE_SHEETS: 'Google Sheets'
};

const SitesView = ({ token, onSitesChange, clientFiltre = null }) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [dataSearchQuery, setDataSearchQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [tri, setTri] = useState({ cle: 'nom', sens: 'asc' });

  /**
   * Les comptes du parc, indexés par **toutes** les écritures sous lesquelles un site peut les
   * avoir rattachés : identifiant de base, numéro de source, et forme qualifiée
   * `matomo-{connexion}-{numéro}` pour Matomo et Piano, dont le numéro n'est unique qu'au sein
   * d'une instance.
   *
   * N'en indexer qu'une ferait afficher une bulle vide sur un site pourtant rattaché — le même
   * défaut silencieux qui montrait « Aucun site associé » sur l'écran Comptes.
   *
   * Un seul appel, et au montage plutôt qu'au survol : la bulle doit s'ouvrir tout de suite, et
   * `/api/library` porte déjà Matomo et Piano. Les trois requêtes de `openDataModal` ne servent
   * qu'à la modale, qui a besoin des sites non encore ajoutés à la bibliothèque.
   */
  const [comptesParIdentifiant, setComptesParIdentifiant] = useState(null);
  const [survol, setSurvol] = useState(null);

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      const response = await fetch(`${config.API_URL}/api/sites`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        setSites(data.sites || []);
      }
    } catch (error) {
      console.error('Erreur chargement sites:', error);
      setError('Erreur lors du chargement des sites');
    } finally {
      setLoading(false);
    }
  };



  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce site ?')) {
      return;
    }

    try {
      const response = await fetch(`${config.API_URL}/api/sites/${siteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        setSites(sites.filter(s => s.id !== siteId));
        setError('');
      }
    } catch (error) {
      console.error('Erreur suppression site:', error);
      setError('Erreur lors de la suppression du site');
    }
  };

  const openEditModal = (site) => {
    // La modale lit le site qu'on lui passe : plus de recopie dans un état local, qui était
    // l'endroit où l'adresse et le type se perdaient.
    setSelectedSite(site);
    setShowEditModal(true);
  };

  useEffect(() => {
    let abandonne = false;

    const charger = async () => {
      try {
        const reponse = await fetch(`${config.API_URL}/api/library`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const donnees = await reponse.json();
        if (abandonne || !donnees.library) return;

        const index = new Map();
        for (const [provider, items] of Object.entries(donnees.library)) {
          for (const item of items || []) {
            const resume = { nom: item.name, provider };
            for (const ecriture of [item.id, item.databaseId, item.libraryId, item.sourceId]) {
              if (ecriture != null) index.set(String(ecriture), resume);
            }

            const prefixe = provider === 'MATOMO' ? 'matomo'
              : provider === 'PIANO_ANALYTICS' ? 'piano'
                : null;
            if (prefixe) {
              const connexion = item.connectionId || 'default';
              // Les deux formes qualifiées rencontrées en base : sur le numéro de site et sur
              // l'identifiant de base, selon l'écran qui a écrit le rattachement.
              for (const fin of [item.sourceId, item.databaseId, item.id]) {
                if (fin != null) index.set(`${prefixe}-${connexion}-${fin}`, resume);
              }
            }
          }
        }
        setComptesParIdentifiant(index);
      } catch (e) {
        // Une bulle qui ne s'ouvre pas est un moindre mal : l'écran doit rester utilisable.
        console.error('Comptes illisibles, la liste des rattachements ne sera pas affichée :', e.message);
        setComptesParIdentifiant(new Map());
      }
    };

    charger();
    return () => { abandonne = true; };
  }, [token]);

  /** Les comptes d'un site, nommés, dans l'ordre où le site les porte. */
  const comptesDuSite = (site) => (site.accountIds || [])
    .map(identifiant => comptesParIdentifiant?.get(String(identifiant)))
    .filter(Boolean);

  const openDataModal = async (site) => {
    setSelectedSite(site);
    setShowDataModal(true);
    setLoadingAccounts(true);

    try {
      // Charger tous les comptes publicitaires disponibles ET les sites Matomo en parallèle
      const [libraryResponse, matomoResponse, pianoResponse] = await Promise.all([
        fetch(`${config.API_URL}/api/library`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${config.API_URL}/api/dashboard/matomo/sites`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => ({ json: () => ({ success: false }) })), // Ignorer les erreurs Matomo
        fetch(`${config.API_URL}/api/dashboard/piano/sites`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => ({ json: () => ({ success: false }) })) // Ignorer les erreurs Piano
      ]);

      const data = await libraryResponse.json();
      const matomoData = await matomoResponse.json();
      const pianoData = await pianoResponse.json();

      if (data.error) {
        setError(data.error);
      } else {
        // L'API retourne un objet groupé par provider, il faut l'aplatir
        const allAccounts = [];
        const seenIds = new Set(); // Pour éviter les doublons

        if (data.library && typeof data.library === 'object') {
          Object.keys(data.library).forEach(provider => {
            // Skip MATOMO et PIANO_ANALYTICS car on les ajoute séparément
            if (provider === 'MATOMO' || provider === 'PIANO_ANALYTICS') return;

            if (Array.isArray(data.library[provider])) {
              data.library[provider].forEach(account => {
                // Éviter les doublons basés sur l'ID
                if (!seenIds.has(account.id)) {
                  seenIds.add(account.id);
                  allAccounts.push({
                    ...account,
                    provider: provider
                  });
                }
              });
            }
          });
        }

        // Ajouter les sites Matomo
        if (matomoData.success && matomoData.sites && Array.isArray(matomoData.sites)) {
          matomoData.sites.forEach(site => {
            // Format cohérent avec App.js: matomo-{connectionId}-{idSite}
            const connId = site.connectionId || 'default';
            const siteId = site.sourceId || site.idSite || site.id;
            const matomoId = `matomo-${connId}-${siteId}`;

            if (!seenIds.has(matomoId)) {
              seenIds.add(matomoId);
              allAccounts.push({
                id: matomoId,
                sourceId: site.sourceId || site.id,
                databaseId: site.id,
                name: site.name,
                provider: 'MATOMO',
                instanceUrl: site.instanceUrl,
                idSite: site.idSite,
                connectionId: site.connectionId
              });
            }
          });
        }

        // Ajouter les sites Piano Analytics
        if (pianoData.success && pianoData.sites && Array.isArray(pianoData.sites)) {
          pianoData.sites.forEach(site => {
            // Format cohérent: piano-{connectionId}-{siteId}
            const connId = site.connectionId || 'default';
            const siteId = site.sourceId || site.siteId || site.id;
            const pianoId = `piano-${connId}-${siteId}`;

            if (!seenIds.has(pianoId)) {
              seenIds.add(pianoId);
              allAccounts.push({
                id: pianoId,
                sourceId: site.sourceId || site.id,
                databaseId: site.id,
                name: site.name,
                provider: 'PIANO_ANALYTICS',
                siteId: site.siteId || site.sourceId,
                connectionId: site.connectionId,
                connectionName: site.connectionName
              });
            }
          });
        }

        // Filtrer les comptes Google Merchant Center du modal
        const filteredAccounts = allAccounts.filter(account => account.provider !== 'GOOGLE_MERCHANT_API_DISABLED');
        setAvailableAccounts(filteredAccounts);
      }
    } catch (error) {
      console.error('Erreur chargement comptes:', error);
      setError('Erreur lors du chargement des comptes');
    } finally {
      setLoadingAccounts(false);
    }
  };

  /**
   * Les régies que le dashboard consolidé additionne.
   *
   * Recopiée de `backend/src/services/consolidatedDaily.js` plutôt qu'importée : le front n'a pas
   * accès aux constantes du serveur, et une liste plus large ferait apparaître une case sur une
   * propriété analytique, où elle n'a aucun sens. Si une régie s'ajoute au consolidé, elle
   * s'ajoute ici.
   */
  const REGIES_CONSOLIDEES = [
    'GOOGLE_ADS', 'MICROSOFT_ADS', 'FACEBOOK_ADS', 'PINTEREST_ADS', 'TIKTOK_ADS', 'OPENAI_ADS', 'AWIN'
  ];

  /**
   * Écarter un compte du consolidé de **ce site**, ou l'y remettre.
   *
   * L'exclusion est portée par le site parce que c'est lui qui rattache le compte à la propriété
   * analytique. Un même compte peut donc compter dans un site et pas dans l'autre : décocher ici
   * ne touche aucun autre périmètre.
   *
   * Les quatre écritures d'un identifiant sont retirées d'un coup — on ne sait pas laquelle le
   * site a employée, et n'en retirer qu'une laisserait l'exclusion en place.
   */
  const basculerConsolide = async (account, etaitEcarte) => {
    try {
      const identifiants = [
        account.databaseId || account.libraryId || account.sourceId || account.id,
        account.sourceId,
        account.id,
        String(account.sourceId)
      ].filter(Boolean);

      const actuels = selectedSite?.accountIdsHorsConsolide || [];
      const nouveaux = etaitEcarte
        ? actuels.filter(id => !identifiants.includes(id))
        : [...actuels, (selectedSite?.accountIds || []).find(id => identifiants.includes(id)) || identifiants[0]];

      const reponse = await fetch(`${config.API_URL}/api/sites/${selectedSite.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: selectedSite.name,
          description: selectedSite.description,
          accountIds: selectedSite.accountIds,
          accountIdsHorsConsolide: [...new Set(nouveaux)]
        })
      });

      const data = await reponse.json();
      if (data.error) return setError(data.error);

      setSites(sites.map(s => (s.id === selectedSite.id ? data : s)));
      setSelectedSite(data);
      setError('');
      if (onSitesChange) onSitesChange();
    } catch (e) {
      console.error('Erreur bascule consolidé:', e);
      setError('Erreur lors de la mise à jour du consolidé');
    }
  };

  const handleToggleAccount = async (account) => {
    if (!selectedSite) return;

    try {
      const currentAccountIds = selectedSite.accountIds || [];

      // Vérifier si le compte est sélectionné avec n'importe quel format d'ID
      const possibleIds = [
        account.databaseId,
        account.libraryId,
        account.sourceId,
        account.id,
        String(account.sourceId)
      ].filter(Boolean);

      const isCurrentlySelected = currentAccountIds.some(id => possibleIds.includes(id));

      let newAccountIds;
      if (isCurrentlySelected) {
        // Retirer tous les IDs qui correspondent à ce compte
        newAccountIds = currentAccountIds.filter(id => !possibleIds.includes(id));
      } else {
        // Pour Merchant Center, utiliser sourceId (merchantId réel) pour le filtrage des flux
        // Pour Matomo et Piano, utiliser l'ID formaté ({provider}-{connectionId}-{siteId})
        // Pour les autres providers, utiliser databaseId ou libraryId
        let newId;
        if (account.provider === 'GOOGLE_MERCHANT_API') {
          newId = account.sourceId || account.databaseId || account.libraryId || account.id;
        } else if (account.provider === 'MATOMO' || account.provider === 'PIANO_ANALYTICS') {
          // Pour Matomo/Piano, l'ID est déjà au bon format: {provider}-{connectionId}-{siteId}
          newId = account.id;
        } else {
          newId = account.databaseId || account.libraryId || account.sourceId || account.id;
        }
        newAccountIds = [...currentAccountIds, newId];
      }

      const response = await fetch(`${config.API_URL}/api/sites/${selectedSite.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: selectedSite.name,
          description: selectedSite.description,
          accountIds: newAccountIds
        })
      });

      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        // Mettre à jour le site dans la liste
        setSites(sites.map(s => s.id === selectedSite.id ? data : s));
        setSelectedSite(data);
        setError('');
        // Notifier App.js que les sites ont changé (pour actualiser userSites)
        if (onSitesChange) {
          onSitesChange();
        }
      }
    } catch (error) {
      console.error('Erreur mise à jour comptes site:', error);
      setError('Erreur lors de la mise à jour des comptes');
    }
  };

  if (loading) {
    return (
      <div className="step-content">
        <div className="loading-state">
          <Loader className="spin" size={24} />
          <p>Chargement des sites...</p>
        </div>
      </div>
    );
  }

  // Une seule liste pour l'affichage et pour le compteur : calculée en deux endroits, elle
  // finirait par afficher un nombre que la liste ne montre pas.
  const recherche = searchQuery.trim().toLowerCase();
  /*
   * Le client choisi dans la barre du haut borne la liste avant la recherche. Un site porte
   * son `clientId` : rien à rapprocher ici, contrairement aux comptes et aux connecteurs.
   */
  const sitesDuPerimetre = clientFiltre
    ? sites.filter(site => site.clientId === clientFiltre)
    : sites;
  const sitesAffiches = [...sitesDuPerimetre]
    .filter(site => !recherche
      || site.name.toLowerCase().includes(recherche)
      || site.description?.toLowerCase().includes(recherche))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

  /**
   * Le tri de la liste. Le nom d'abord, comme avant la refonte, mais les trois autres colonnes
   * se trient aussi : à trente sites, ranger par nombre de comptes ou par date répond à des
   * questions que la grille de cartes ne permettait pas de poser.
   */
  const comparer = (a, b) => {
    const sens = tri.sens === 'asc' ? 1 : -1;
    if (tri.cle === 'comptes') return sens * ((a.accountIds?.length || 0) - (b.accountIds?.length || 0));
    if (tri.cle === 'date') return sens * (new Date(a.createdAt) - new Date(b.createdAt));
    return sens * a.name.localeCompare(b.name, 'fr');
  };

  const sitesTries = [...sitesAffiches].sort(comparer);

  const COLONNES = [
    { cle: 'nom', libelle: 'Site' },
    { cle: 'comptes', libelle: 'Comptes' },
    { cle: 'date', libelle: 'Créé le' }
  ];

  return (
    <div className="step-content kc-page">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}><Globe size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} /> Sites</h2>
        {sites.length > 0 && (
          <span className="kok-compteur">
            {recherche
              ? `${sitesAffiches.length} sur ${sitesDuPerimetre.length}`
              : `${sitesDuPerimetre.length} site${sitesDuPerimetre.length > 1 ? 's' : ''}`}
          </span>
        )}
        {sites.length > 0 && (
          <div className="kc-recherche">
            <Search size={16} />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" className="kc-recherche-effacer" onClick={() => setSearchQuery('')} title="Effacer">
                <X size={14} />
              </button>
            )}
          </div>
        )}
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary btn-small"
          style={{ marginLeft: 'auto', backgroundColor: '#111827', borderColor: '#111827' }}
        >
          + Ajouter un site
        </button>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div className="kc-liste ksi-liste">
        {sites.length === 0 ? (
          <div className="kc-vide">
            <Globe size={40} />
            <p className="kc-vide-titre">Aucun site</p>
            <p className="kc-vide-texte">
              Un site réunit les comptes d'un même périmètre — la régie, la solution de mesure —
              et c'est lui qui permet au dashboard consolidé de les croiser.
            </p>
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary btn-small">
              + Ajouter un site
            </button>
          </div>
        ) : sitesDuPerimetre.length === 0 ? (
          /* « Vide à cause du filtre » n'est pas « aucun résultat de recherche » : sans cette
             branche, l'écran reprocherait une recherche que personne n'a tapée. */
          <div className="kc-vide">
            <Globe size={40} />
            <p className="kc-vide-titre">Aucun site pour ce client</p>
            <p className="kc-vide-texte">
              Le client choisi dans la barre du haut n'a encore aucun site rattaché.
            </p>
          </div>
        ) : sitesTries.length === 0 ? (
          <div className="kc-vide">
            <Search size={40} />
            <p className="kc-vide-titre">Aucun résultat</p>
            <p className="kc-vide-texte">Aucun site ne correspond à « {searchQuery} ».</p>
          </div>
        ) : (
          <>
            <div className="kc-grille ksi-grille kc-entete">
              {COLONNES.map(colonne => (
                <button
                  key={colonne.cle}
                  type="button"
                  className={`kc-tri${tri.cle === colonne.cle ? ' actif' : ''}`}
                  title={`Trier par ${colonne.libelle.toLowerCase()}`}
                  onClick={() => setTri(a => (
                    a.cle === colonne.cle
                      ? { cle: colonne.cle, sens: a.sens === 'asc' ? 'desc' : 'asc' }
                      : { cle: colonne.cle, sens: 'asc' }
                  ))}
                >
                  {colonne.libelle}
                  {tri.cle === colonne.cle
                    ? <ChevronDown size={12} style={{ transform: tri.sens === 'desc' ? 'rotate(180deg)' : 'none' }} />
                    : <ChevronDown size={12} className="kc-tri-repos" />}
                </button>
              ))}
              <span className="kc-entete-fin">Actions</span>
            </div>

            {sitesTries.map(site => (
              <div key={site.id} className="kc-grille ksi-grille kc-ligne">
                <div className="kc-marque">
                  <span className="kc-logo"><Globe size={17} /></span>
                  <span style={{ minWidth: 0 }}>
                    <span className="kc-nom" style={{ display: 'block' }}>
                      {site.name}
                      {/* Le type, collé au nom : c'est une propriété du site, pas une donnée
                          mesurée, et lui donner une colonne la mettrait sur le même plan que les
                          comptes rattachés. Rien quand il n'est pas déclaré — une pastille
                          « non défini » signalerait comme un manque un champ facultatif. */}
                      {site.typeSite && (
                        <span className="ksi-type">
                          {site.typeSite === 'ECOMMERCE' ? 'E-commerce' : 'Lead'}
                        </span>
                      )}
                    </span>
                    {/* L'adresse, cliquable. `stopPropagation` parce que la ligne entière est
                        cliquable : sans lui, ouvrir le site ouvrirait aussi la fiche derrière.
                        `noopener` parce que la page ouverte hérite sinon d'une référence à
                        celle-ci. */}
                    {site.url && (
                      <a
                        className="kc-qualificatif ksi-url"
                        href={site.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title={site.url}
                      >
                        {site.url.replace(/^https?:\/\//i, '').replace(/\/$/, '')}
                      </a>
                    )}
                  </span>
                </div>

                <span
                  className={`kli-compte${site.accountIds?.length ? ' ksi-survolable' : ''}`}
                  onMouseEnter={(e) => {
                    if (!site.accountIds?.length) return;
                    const zone = e.currentTarget.getBoundingClientRect();
                    // Position figée à l'entrée : la bulle sort par un portail, et la liste a
                    // `overflow: hidden` — un panneau posé dans la ligne serait rogné. Les deux
                    // bords sont retenus : le bas pour poser la bulle dessous, le haut pour la
                    // retourner au-dessus quand elle ne tient pas en bas d'écran.
                    setSurvol({ siteId: site.id, x: zone.left, bas: zone.bottom + 6, haut: zone.top - 6 });
                  }}
                  onMouseLeave={() => setSurvol(null)}
                >
                  <Database size={14} />
                  {site.accountIds?.length || 0} compte{(site.accountIds?.length || 0) > 1 ? 's' : ''}
                </span>

                <span className="kc-date">
                  {new Date(site.createdAt).toLocaleDateString('fr-FR')}
                </span>

                <div className="kc-actions">
                  <button
                    type="button"
                    className="kc-action"
                    onClick={() => openDataModal(site)}
                    title="Gérer les données rattachées à ce site"
                  >
                    <Database size={15} />
                  </button>
                  <button type="button" className="kc-action" onClick={() => openEditModal(site)} title="Modifier">
                    <Edit2 size={15} />
                  </button>
                  <button
                    type="button"
                    className="kc-action danger"
                    onClick={() => handleDeleteSite(site.id)}
                    title="Supprimer ce site"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* La bulle des comptes rattachés, par portail : `.kc-liste` a `overflow: hidden`, et un
          panneau posé dans la ligne serait coupé dès la deuxième ligne. */}
      {survol && ReactDOM.createPortal(
        <BulleSurvol position={survol}>
          {(() => {
            const comptes = comptesParIdentifiant === null
              ? null
              : comptesDuSite(sites.find(s => s.id === survol.siteId) || {});
            if (comptes === null) return <span className="kbulle-vide">Chargement…</span>;
            if (comptes.length === 0) {
              return <span className="kbulle-vide">Aucun compte retrouvé dans la bibliothèque.</span>;
            }
            return comptes.map((compte, index) => (
              <div key={index} className="kbulle-ligne">
                <span className="kbulle-logo">
                  {LOGOS_PROVIDER[compte.provider]
                    ? <img src={LOGOS_PROVIDER[compte.provider]} alt="" />
                    : <Database size={13} />}
                </span>
                <span className="kbulle-nom">{compte.nom}</span>
                <span className="kbulle-type">{LIBELLES_PROVIDER[compte.provider] || compte.provider}</span>
              </div>
            ));
          })()}
        </BulleSurvol>,
        document.body
      )}

      {/* Modal de création */}
      {/*
        La même modale que le Gestionnaire, et non une copie.
        Cet écran avait la sienne — « Nouveau Site », avec un champ Description — et les deux
        avaient déjà divergé : l'une demandait l'adresse et le type du site, l'autre une
        description que plus personne n'affichait. Un composant partagé ferme la question.
      */}
      <CreateSiteModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        token={token}
        onCreated={loadSites}
      />

      {/* Modal de modification */}
      {/* Même composant que la création : voir le commentaire de `CreateSiteModal`. */}
      <CreateSiteModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedSite(null); }}
        token={token}
        site={selectedSite}
        onCreated={loadSites}
      />

      {/* Modal de gestion des données */}
      {showDataModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => { setShowDataModal(false); setDataSearchQuery(''); }} style={{ zIndex: 9999 }}>
          <div className="modal large-modal data-management-modal" onClick={e => e.stopPropagation()} style={{
            width: '580px',
            maxWidth: '90vw',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div className="modal-header" style={{ padding: '16px !important', margin: '0 !important' }}>
              <h2>Associer des comptes au site {selectedSite?.name}</h2>
              <button
                onClick={() => { setShowDataModal(false); setDataSearchQuery(''); }}
                className="btn-close"
              >
                ×
              </button>
            </div>
            <>
              <div className="modal-content" style={{
                padding: '8px 24px 16px 24px',
                margin: '0'
              }}>
              {error && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
              
              {loadingAccounts ? (
                <div className="loading-state">
                  <Loader className="spin" size={24} />
                  <p>Chargement des comptes...</p>
                </div>
              ) : (
                <>
                  {/* Champ de recherche */}
                  <div style={{
                    position: 'relative',
                    marginBottom: '16px',
                    width: '100%'
                  }}>
                    <Search
                      size={18}
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9ca3af'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Rechercher un compte..."
                      value={dataSearchQuery}
                      onChange={(e) => setDataSearchQuery(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px 10px 40px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#1a1a1a'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>

                  {!Array.isArray(availableAccounts) || availableAccounts.length === 0 ? (
                    <div className="empty-state">
                      <Database size={48} />
                      <h3>Aucun compte publicitaire</h3>
                      <p>Vous devez d'abord connecter vos comptes publicitaires dans la section Connecteurs.</p>
                    </div>
                  ) : (
                    <div className="accounts-container" style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      maxHeight: '400px',
                      overflowY: 'auto'
                    }}>
                      {Array.isArray(availableAccounts) &&
                       availableAccounts
                         // Filtrer par recherche
                         .filter(account => {
                           if (!dataSearchQuery.trim()) return true;
                           const query = dataSearchQuery.toLowerCase();
                           const accountId = account.sourceId || account.id || '';
                           return account.name.toLowerCase().includes(query) ||
                                  String(accountId).toLowerCase().includes(query);
                         })
                         // Trier : sélectionnés en premier, puis alphabétique
                         .sort((a, b) => {
                           const accountIds = selectedSite?.accountIds || [];
                           const isAccountSelected = (acc) =>
                             accountIds.includes(acc.databaseId) ||
                             accountIds.includes(acc.libraryId) ||
                             accountIds.includes(acc.sourceId) ||
                             accountIds.includes(acc.id) ||
                             accountIds.includes(String(acc.sourceId));
                           const aSelected = isAccountSelected(a);
                           const bSelected = isAccountSelected(b);
                           if (aSelected && !bSelected) return -1;
                           if (!aSelected && bSelected) return 1;
                           return a.name.localeCompare(b.name);
                         })
                         .map(account => {
                        // Utiliser databaseId (UUID unique) pour les nouvelles associations
                        const accountIdentifier = account.databaseId || account.libraryId || account.sourceId || account.id;
                        // Vérifier si sélectionné avec l'ancien OU le nouveau format
                        const accountIds = selectedSite?.accountIds || [];
                        const isSelected = accountIds.includes(accountIdentifier) ||
                                          accountIds.includes(account.sourceId) ||
                                          accountIds.includes(account.id) ||
                                          accountIds.includes(String(account.sourceId));

                        /**
                         * Le consolidé n'additionne que des coûts de régie : une propriété
                         * analytique n'a rien à y écarter, et Merchant Center ni Search Console
                         * n'y entrent pas non plus. La case n'apparaît donc que pour les régies.
                         */
                        const consolidable = REGIES_CONSOLIDEES.includes(account.provider);
                        const ecartes = selectedSite?.accountIdsHorsConsolide || [];
                        const estEcarte = consolidable && [
                          accountIdentifier, account.sourceId, account.id, String(account.sourceId)
                        ].some(id => id && ecartes.includes(id));
                        
                        // Fonction pour obtenir le logo approprié
                        const getLogo = (provider) => {
                          switch(provider) {
                            case 'GOOGLE_ADS':
                              return googleAdsLogo;
                            case 'MICROSOFT_ADS':
                              return microsoftAdsLogo;
                            case 'FACEBOOK_ADS':
                              return metaAdsLogo;
                            case 'OPENAI_ADS':
                              return chatgptAdsLogo;
                            case 'AWIN':
                              return awinLogo;
                            case 'PRESTASHOP':
                              return prestashopLogo;
                            case 'TIKTOK_ADS':
                              return tiktokAdsLogo;
                            case 'PINTEREST_ADS':
                              return pinterestAdsLogo;
                            case 'GOOGLE_MERCHANT_API':
                              return 'merchant_icon'; // Marqueur spécial pour utiliser une icône
                            case 'MATOMO':
                              return 'matomo_icon'; // Marqueur spécial pour Matomo
                            case 'PIANO_ANALYTICS':
                              return 'piano_icon'; // Marqueur spécial pour Piano Analytics
                            case 'GOOGLE_ANALYTICS':
                              return 'ga4_icon'; // Marqueur spécial pour GA4
                            case 'GOOGLE_SEARCH_CONSOLE':
                              return 'gsc_icon'; // Marqueur spécial pour GSC
                            case 'GOOGLE_TAG_MANAGER':
                              // Pas de marqueur : le logo est un PNG importé, et le repli
                              // générique plus bas le rend tel quel.
                              return tagManagerLogo;
                            default:
                              return null;
                          }
                        };
                        
                        return (
                          <div key={account.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            boxSizing: 'border-box',
                            marginBottom: '8px',
                            backgroundColor: isSelected ? '#FAF9F5' : 'white',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            padding: '12px 16px',
                            gap: '12px'
                          }}>
                            {/* Logo */}
                            <div style={{
                              width: '32px',
                              height: '32px',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {getLogo(account.provider) === 'merchant_icon' ? (
                                <div style={{
                                  width: '28px',
                                  height: '28px',
                                  backgroundColor: '#4285f4',
                                  borderRadius: '6px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white'
                                }}>
                                  <ShoppingBag size={16} />
                                </div>
                              ) : getLogo(account.provider) === 'matomo_icon' ? (
                                <img
                                  src={matomoLogo}
                                  alt="Matomo"
                                  style={{ width: '28px', height: '28px', objectFit: 'contain' }}
                                />
                              ) : getLogo(account.provider) === 'ga4_icon' ? (
                                <img
                                  src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png"
                                  alt="Google Analytics 4"
                                  style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '4px' }}
                                />
                              ) : getLogo(account.provider) === 'gsc_icon' ? (
                                <img
                                  src={searchConsoleLogo}
                                  alt="Google Search Console"
                                  style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '4px' }}
                                />
                              ) : getLogo(account.provider) === 'piano_icon' ? (
                                <img
                                  src={pianoLogo}
                                  alt="Piano Analytics"
                                  style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '6px' }}
                                />
                              ) : getLogo(account.provider) ? (
                                <img
                                  src={getLogo(account.provider)}
                                  alt={account.provider}
                                  style={{ width: '28px', height: '28px', objectFit: 'contain' }}
                                />
                              ) : (
                                <div style={{
                                  width: '28px',
                                  height: '28px',
                                  backgroundColor: '#f3f4f6',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  color: '#666'
                                }}>
                                  ?
                                </div>
                              )}
                            </div>

                            {/* Texte */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: '500', fontSize: '14px', lineHeight: '1.3' }}>{account.name}</div>
                              <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.3' }}>
                                {/*
                                  * La table, et non une chaîne de ternaires qui disait la même
                                  * chose treize fois : les deux avaient déjà divergé — Tag Manager
                                  * figurait dans `LIBELLES_PROVIDER` et pas ici, si bien que la
                                  * ligne affichait « GOOGLE_TAG_MANAGER » en toutes lettres.
                                  * Le repli reste l'identifiant brut : mieux vaut un nom technique
                                  * qu'une case vide.
                                  */}
                                {LIBELLES_PROVIDER[account.provider] || account.provider}
                              </div>
                            </div>

                            {/* Alimente-t-il le consolidé ? Seulement pour un compte déjà
                                rattaché : la question ne se pose pas avant. */}
                            {isSelected && consolidable && (
                              <label
                                className={`site-consolide${estEcarte ? ' ecarte' : ''}`}
                                title={estEcarte
                                  ? "Ce compte est rattaché au site mais n'est pas additionné dans le dashboard consolidé. Il continue d'alimenter son dashboard, les outils et le MCP."
                                  : 'Ce compte est additionné dans le dashboard consolidé de ce site.'}
                              >
                                <input
                                  type="checkbox"
                                  checked={!estEcarte}
                                  onChange={() => basculerConsolide(account, estEcarte)}
                                />
                                consolidé
                              </label>
                            )}

                            {/* Bouton */}
                            <button
                              onClick={() => handleToggleAccount(account)}
                              className={`btn-toggle ${isSelected ? 'active' : ''}`}
                              style={{ flexShrink: 0 }}
                            >
                              {isSelected ? 'Retirer' : 'Ajouter'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '16px !important', margin: '0 !important' }}>
              <button
                type="button"
                onClick={() => { setShowDataModal(false); setDataSearchQuery(''); }}
                className="btn btn-primary"
              >
                Fermer
              </button>
            </div>
            </>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SitesView;